import { useState } from 'react';
import clsx from '../utils/clsx';
import styles from '../styles/Battlefield.module.css';
import CharacterPreview from './CharacterPreview';

/**
 * WHO STANDS IN. Armed, the next click on a marker opens the replace
 * dialog instead of planting the flag under them; the drop-on-token
 * gesture is the other door to the same verb. It sits here, with the wings
 * the newcomer comes from, because the deploy bar is two wrapped rows at a
 * narrow centre column and one more control pushed it into the map
 * (v8.344.2).
 */
function ReplaceButton({ armed, onReplace }) {
  return (
    <button
      type="button"
      className={clsx(styles.roomDeploy, armed && styles.roomReplaceArmed)}
      data-testid="deploy-replace"
      aria-pressed={armed}
      aria-label={armed ? 'Pick a marker to replace' : 'Replace somebody on the map'}
      title={
        armed
          ? 'Click a marker to replace them - or click again to stop'
          : 'Replace somebody on the map with a waiting or library character'
      }
      onClick={onReplace}
    >
      ⇄
    </button>
  );
}

/**
 * A NAME DROPPED ON A ROOM MOVES; dropped on the map it deploys. One
 * gesture, and the target says what it means (v8.25.0). The row under the
 * pointer mid-drag is lit, so the drop target is visible before you let go.
 */
function roomDropHandlers(room, onMoveToRoom, setOverRoom) {
  if (!onMoveToRoom) return { onDragOver: undefined, onDrop: undefined };
  return {
    onDragOver: (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setOverRoom(room.id);
    },
    onDrop: (e) => {
      e.preventDefault();
      setOverRoom(null);
      const id = e.dataTransfer.getData('text/plain');
      if (id) onMoveToRoom(id, room.id);
    },
  };
}

/**
 * Deploying the WHOLE room, for whoever would rather press a button than
 * make a gesture. The gestures take from the active room; this takes from
 * the one you clicked.
 */
function DeployRoomButton({ room, disabled, onDeployRoom }) {
  const n = room.entries.length;
  return (
    <button
      type="button"
      className={styles.roomDeploy}
      data-testid={`room-deploy-${room.id}`}
      disabled={disabled || n === 0}
      title={disabled ? 'Plant the deployment marker first' : `Ring all ${n} around the marker`}
      aria-label={`Deploy everyone in ${room.name}`}
      onClick={() => onDeployRoom?.(room.id)}
    >
      ⚑
    </button>
  );
}

/**
 * ✕ ON A ROOM. It asks only when somebody is inside - an empty room is a
 * name, and losing a name you can retype is not worth a dialogue (v8.24.0).
 */
function DeleteRoomButton({ room, onDeleteRoom }) {
  const n = room.entries.length;
  return (
    <button
      type="button"
      className={styles.roomDelete}
      data-testid={`room-delete-${room.id}`}
      aria-label={`Delete ${room.name}`}
      title={n ? `Delete ${room.name} and its ${n} waiting` : 'Delete'}
      onClick={() => onDeleteRoom(room.id)}
    >
      ✕
    </button>
  );
}

function RoomRow({ room, rail, overRoom, setOverRoom }) {
  const { activeId } = rail;
  return (
    <div
      className={clsx(styles.roomRowWrap, overRoom === room.id && styles.roomRowOver)}
      {...roomDropHandlers(room, rail.onMoveToRoom, setOverRoom)}
      onDragLeave={() => setOverRoom((cur) => (cur === room.id ? null : cur))}
    >
      <button
        type="button"
        role="option"
        aria-selected={room.id === activeId}
        className={clsx(styles.roomRow, room.id === activeId && styles.roomRowActive)}
        data-testid={`room-${room.id}`}
        onClick={() => rail.onActive?.(room.id)}
        title={`${room.name} - ${room.entries.length} waiting`}
      >
        <span className={styles.roomName}>{room.name}</span>
        <span className={styles.roomCount}>{room.entries.length}</span>
      </button>
      <DeployRoomButton room={room} disabled={rail.disabled} onDeployRoom={rail.onDeployRoom} />
      {rail.onDeleteRoom && <DeleteRoomButton room={room} onDeleteRoom={rail.onDeleteRoom} />}
    </div>
  );
}

/**
 * One waiting combatant: drag them onto the map to place them there, or
 * onto another room to move them. WHO THEY ARE, before they walk on - a
 * staged combatant is invisible everywhere else by design, so building an
 * ambush was blind (v8.25.0); hovering shows the library's own card.
 * Plain text in the drag, because that is what a drop handler can read
 * back on any browser without a custom MIME type.
 */
function RoomMember({ c, onDismiss, setPreview }) {
  return (
    <div
      draggable
      data-testid={`room-member-${c.id}`}
      className={styles.roomMember}
      title={`Drag ${c.name} onto the map to place them there, or onto another room to move them`}
      onMouseEnter={(e) => setPreview({ id: c.id, rect: e.currentTarget.getBoundingClientRect() })}
      onMouseLeave={() => setPreview((p) => (p?.id === c.id ? null : p))}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', c.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
    >
      <span className={styles.roomMemberName}>{c.name}</span>
      {onDismiss && (
        <button
          type="button"
          className={styles.roomDismiss}
          data-testid={`room-dismiss-${c.id}`}
          aria-label={`Dismiss ${c.name}`}
          title="Take them out of the room - the character file is untouched"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss(c.id);
          }}
        >
          ✕
        </button>
      )}
    </div>
  );
}

/**
 * WHO IS IN THE ACTIVE ROOM. A count says a room is loaded; a list is what
 * lets you pull one person out of it and put them somewhere exact. Only
 * the active room expands - six rooms of names would be a wall.
 */
function RoomMembers({ active, onDismiss, setPreview }) {
  if (!active || active.entries.length === 0) return null;
  return (
    <div className={styles.roomMembers} data-testid="room-members">
      {active.entries.map((c) => (
        <RoomMember key={c.id} c={c} onDismiss={onDismiss} setPreview={setPreview} />
      ))}
    </div>
  );
}

/**
 * The waiting rooms, down the side of Deploy mode.
 *
 * Opposite the tool rail, where the level picker sits in Play - the
 * same place for the same reason: it belongs to the mode rather than to
 * the map, and the map's own furniture is already spoken for.
 *
 * ONLY ONE ROOM IS ACTIVE. Every deploy gesture takes from it, so
 * "which room" is answered once, here, rather than in each of the four
 * gestures. A room with nobody in it is still shown: it is a place you
 * named, and it outlives whoever was standing in it.
 *
 * This is the only surface in the app that says a staged combatant
 * exists - by design. Whoever cannot see this rail cannot read the
 * documents behind it either, which is enforced in `firestore.rules`.
 */
export default function BattlefieldRoomRail(props) {
  const { rooms = [], activeId, onNewRoom, onReplace, replaceArmed = false } = props;
  const rail = { ...props, disabled: props.disabled ?? false };
  const active = rooms.find((r) => r.id === activeId) ?? null;
  const [overRoom, setOverRoom] = useState(null);
  const [preview, setPreview] = useState(null);
  // A room holds whole Combatants, so the sheet is right there - the same
  // card the character menu draws, fed the same shape.
  const previewOf = preview ? (active?.entries.find((c) => c.id === preview.id)?.sheet ?? null) : null;
  return (
    <div className={styles.roomRail} data-testid="battlefield-rooms">
      <div className={styles.roomRailHead}>
        Waiting
        {onReplace && <ReplaceButton armed={replaceArmed} onReplace={onReplace} />}
      </div>
      <div className={styles.roomList} role="listbox" aria-label="Waiting rooms">
        {rooms.length === 0 && <div className={styles.roomEmpty}>No rooms yet</div>}
        {rooms.map((room) => (
          <RoomRow key={room.id} room={room} rail={rail} overRoom={overRoom} setOverRoom={setOverRoom} />
        ))}
        <RoomMembers active={active} onDismiss={props.onDismiss} setPreview={setPreview} />
      </div>
      <button type="button" className={styles.roomNew} data-testid="room-new" onClick={() => onNewRoom?.()}>
        + Room
      </button>
      {previewOf && <CharacterPreview character={previewOf} anchor={preview.rect} />}
    </div>
  );
}
